import { LoyaltyBadges } from "@/components/loyalty-badges";
import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useBookingJobStore } from "@/stores/use-booking-job-store";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Image } from "expo-image";
import React from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";

interface CustomerInfoCardProps {
  name: string;
  roleLabel: string;
  sectionTitle?: string;
  subtitle?: string;
  avatar?: string;
  rating?: string | number;
  loyaltyTier?: string | null;
  isVerified?: boolean;
  onChat?: () => void;
  onCall?: () => void;
  showActions?: boolean;
  variant?: "list" | "detail";
}

export const CustomerInfoCard: React.FC<CustomerInfoCardProps> = ({
  name,
  roleLabel,
  sectionTitle,
  subtitle,
  avatar,
  rating,
  loyaltyTier,
  isVerified = false,
  onChat,
  onCall,
  showActions = false,
  variant = "list",
}) => {
  const { theme } = useAuth();
  const newMessage = useBookingJobStore((state) => state.newmessage);
  const setNewMessage = useBookingJobStore((state) => state.setNewMessage);

  const handleChatPress = () => {
    setNewMessage(false);
    onChat?.();
  };
  const resolvedSectionTitle =
    sectionTitle ??
    (roleLabel.toLowerCase() === "driver"
      ? "Operator information"
      : "Customer information");

  return (
    <View>
      {variant === "list" ? (
        <View style={styles.sectionHeader}>
          <Ionicons
            name="person-outline"
            size={18}
            color={BrandColors.primary}
          />
          <ThemedText style={styles.sectionTitle} type="defaultSemiBold">
            {resolvedSectionTitle}
          </ThemedText>
        </View>
      ) : null}
      <View
        style={[
          styles.card,
          {
            backgroundColor: theme.card,
            borderColor: theme.border,
            shadowColor: theme.background,
          },
        ]}
      >
        <View style={styles.profileRow}>
          {avatar ? (
            <Image
              source={{ uri: avatar }}
              style={styles.avatar}
              contentFit="cover"
            />
          ) : (
            <View style={[styles.avatar, styles.avatarPlaceholder]}>
              <Ionicons
                name="person"
                size={24}
                color={BrandColors.mediumGray}
              />
            </View>
          )}

          <View style={styles.profileInfo}>
            <View style={styles.nameRow}>
              <ThemedText
                style={styles.name}
                type="defaultSemiBold"
                numberOfLines={1}
              >
                {name}
              </ThemedText>
              {rating ? (
                <View style={styles.ratingRow}>
                  <Ionicons name="star" size={14} color="#FBBF24" />
                  <ThemedText
                    style={styles.ratingText}
                    type="extraSmallSemiBold"
                  >
                    {rating}
                  </ThemedText>
                </View>
              ) : null}
            </View>

            <ThemedText style={styles.subtitle} type="extraSmall">
              {subtitle ?? roleLabel}
            </ThemedText>

            <LoyaltyBadges
              loyaltyTier={loyaltyTier}
              isTopRated={isVerified}
            />
          </View>

          {showActions ? (
            <View style={styles.actions}>
              {onCall ? (
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={onCall}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="call-outline"
                    size={18}
                    color={BrandColors.primary}
                  />
                </TouchableOpacity>
              ) : null}
              {onChat ? (
                <View style={styles.actionButtonWrapper}>
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={handleChatPress}
                    activeOpacity={0.7}
                  >
                    <Ionicons
                      name="chatbubble-outline"
                      size={18}
                      color={BrandColors.primary}
                    />
                  </TouchableOpacity>
                  {newMessage ? <View style={styles.chatBadge} /> : null}
                </View>
              ) : null}
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "700",
  },
  card: {
    borderRadius: 16,
    padding: 14,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
    borderWidth: 1,
  },
  profileRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
  },
  avatarPlaceholder: {
    backgroundColor: BrandColors.backgroundGray,
    alignItems: "center",
    justifyContent: "center",
  },
  profileInfo: {
    flex: 1,
    gap: 4,
  },
  nameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  name: {
    fontSize: 16,
    fontWeight: "700",
  },
  ratingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  ratingText: {
    fontSize: 13,
    fontWeight: "700",
  },
  subtitle: {
    color: BrandColors.mediumGray,
    textTransform: "capitalize",
  },
  actions: {
    flexDirection: "row",
    gap: 8,
  },
  actionButtonWrapper: {
    position: "relative",
  },
  chatBadge: {
    position: "absolute",
    top: 0,
    right: 0,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#EF4444",
    borderWidth: 1.5,
    borderColor: BrandColors.white,
  },
  actionButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: BrandColors.primary,
    alignItems: "center",
    justifyContent: "center",
  },
});
